import { type ComponentProps, type CSSProperties, type HTMLAttributes, type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

/** The browser tab's title while the page is shown: "Worklist · TBIS". */
export function useDocumentTitle(title: string | undefined) {
  const { t } = useTranslation();
  const product = t("app.product");
  useEffect(() => {
    if (!title) return;
    document.title = `${title} · ${product}`;
    return () => {
      document.title = product;
    };
  }, [title, product]);
}

export function Card({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("rounded-card border border-line bg-surface shadow-card", className)} {...props} />;
}

export function CardHeader({
  title,
  icon,
  actions,
  className,
  children,
}: {
  title: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-b border-line px-[18px] py-3.5", className)}>
      {icon && <span className="text-brand [&_svg]:size-[18px]">{icon}</span>}
      <h2 className="m-0 text-[15px] font-[650]">{title}</h2>
      {children}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-[18px]", className)} {...props} />;
}

export function PageHeader({
  title,
  subtitle,
  crumbs,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  crumbs?: ReactNode;
  actions?: ReactNode;
}) {
  useDocumentTitle(typeof title === "string" ? title : undefined);
  return (
    <div className="mb-[18px]">
      {crumbs && <div className="mb-1.5 text-[13px] text-ink-3">{crumbs}</div>}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="m-0 text-[22px] leading-tight font-[650] tracking-[-0.01em]">{title}</h1>
          {subtitle && <div className="mt-1 text-ink-3">{subtitle}</div>}
        </div>
        {actions && <div className="ml-auto flex flex-wrap gap-2 max-sm:w-full">{actions}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, children }: { icon?: ReactNode; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex animate-fade-in flex-col items-center px-6 py-14 text-center">
      {icon && (
        <div className="mb-4 grid size-12 animate-pop place-items-center rounded-xl bg-brand-50 text-brand ring-8 ring-brand-50/50 [&_svg]:size-6">{icon}</div>
      )}
      <h3 className="m-0 text-base font-semibold">{title}</h3>
      {children && <div className="mt-1.5 max-w-md text-ink-3">{children}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn("inline-block size-5 animate-spin rounded-full border-2 border-brand-100 border-t-brand", className)}
    />
  );
}

/** A placeholder block with a soft shimmer, shaped like the content that is loading. */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <span
      aria-hidden
      style={style}
      className={cn(
        "block h-3.5 animate-shimmer rounded-md bg-[linear-gradient(90deg,#eceff3_30%,#f6f7f9_50%,#eceff3_70%)] bg-[length:200%_100%]",
        className,
      )}
    />
  );
}

/** Announces "Loading…" once to screen readers; sighted users see the skeleton inside. */
export function Loading({ className, children }: { className?: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("common.loading")} aria-busy className={cn("animate-fade-in", className)}>
      {children}
    </div>
  );
}

/** Rows of a list or table that is loading. */
export function SkeletonRows({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <Loading className={className}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-line px-[18px] py-3.5 last:border-b-0" style={{ opacity: 1 - i * (0.6 / rows) }}>
          <div className="grid flex-1 gap-2">
            <Skeleton className="w-[38%] min-w-28" />
            <Skeleton className="h-2.5 w-[22%] min-w-20" />
          </div>
          <Skeleton className="hidden h-2.5 w-24 sm:block" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      ))}
    </Loading>
  );
}

/** A card whose content is loading: a title and a few lines. */
export function SkeletonCard({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <Card className={className}>
      <Loading>
        <div className="border-b border-line px-[18px] py-4">
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="grid gap-3 p-[18px]">
          {Array.from({ length: lines }, (_, i) => (
            <Skeleton key={i} className={["w-[92%]", "w-[76%]", "w-[84%]", "w-[60%]"][i % 4]} />
          ))}
        </div>
      </Loading>
    </Card>
  );
}
